import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";

import { translate } from "../../../hooks/translate";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { withAuth } from "../../../components/HOC/withAuth";
import { formatInstantDateShortTr } from "../../../utils/appTime";

import AppPage from "../../../components/CustomUI/AppPage";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalBody,
  ModalClose,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import modalStyles from "../../../components/BaseUI/Modal/Modal.module.scss";
import ButtonGroup from "../../../components/BaseUI/ButtonGroup";
import Button from "../../../components/BaseUI/Button";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";
import Label from "../../../components/BaseUI/Label";
import { FiCheckCircle } from "react-icons/fi";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";

import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import {
  buildPickerFilterSection,
  countTruthy,
} from "../shared/buildListFilterSections";
import Pagination from "../../../components/BaseUI/Pagination";
import Input from "../../../components/BaseUI/Input";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const AssemblyProducts = () => {
  const [assemblyInventory, setAssemblyInventory] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [inventoryToUpdate, setInventoryToUpdate] = useState(null);
  const [models, setModels] = useState([]);
  const [modelFilter, setModelFilter] = useState(null);
  const [sort, setSort] = useState({ sortBy: "createdAt", sortOrder: "desc" });
  const [serialNumberInput, setSerialNumberInput] = useState("");
  const [scannedSerials, setScannedSerials] = useState([]);
  const [existingDevices, setExistingDevices] = useState([]);
  const [loadingExistingDevices, setLoadingExistingDevices] = useState(false);
  const [autoGenerateQuantity, setAutoGenerateQuantity] = useState("");
  const [showAddSidebar, setShowAddSidebar] = useState(false);
  const [selectedModelForAdd, setSelectedModelForAdd] = useState(null);
  const [quantityToAdd, setQuantityToAdd] = useState("");

  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/assembly-products",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  useTitle("Rovion MES - Montaj");

  const splitAlphaNum = (s) => {
    const str = (s ?? "").toString();
    if (!str) return [];
    return str.match(/[A-Za-z]+|\d+|[^A-Za-z\d]+/g) || [];
  };

  // A -> Z, char-by-char, letters come before numbers
  const compareCharByCharAZ = (a, b) => {
    const ax = splitAlphaNum(a);
    const bx = splitAlphaNum(b);
    const n = Math.max(ax.length, bx.length);

    for (let i = 0; i < n; i++) {
      const as = ax[i] ?? "";
      const bs = bx[i] ?? "";
      if (!as && !bs) return 0;
      if (!as) return -1;
      if (!bs) return 1;

      const aIsDigits = /^\d+$/.test(as);
      const bIsDigits = /^\d+$/.test(bs);
      const aIsLetters = /^[A-Za-z]+$/.test(as);
      const bIsLetters = /^[A-Za-z]+$/.test(bs);

      const typeRank = (isLetters, isDigits) =>
        isLetters ? 0 : isDigits ? 1 : 2;
      const ar = typeRank(aIsLetters, aIsDigits);
      const br = typeRank(bIsLetters, bIsDigits);
      if (ar !== br) return ar - br;

      if (aIsDigits && bIsDigits) {
        const an = Number(as);
        const bn = Number(bs);
        if (an !== bn) return an - bn;
        if (as.length !== bs.length) return as.length - bs.length;
        if (as !== bs)
          return as.localeCompare(bs, "en", { sensitivity: "base" });
        continue;
      }

      const cmp = as.localeCompare(bs, "en", { sensitivity: "base" });
      if (cmp !== 0) return cmp;
    }
    return 0;
  };

  const sortedModels = useMemo(() => {
    return [...(models || [])].sort((a, b) => {
      const byCode = compareCharByCharAZ(a?.code || "", b?.code || "");
      if (byCode !== 0) return byCode;
      return compareCharByCharAZ(a?.name || "", b?.name || "");
    });
  }, [models]);

  const columns = [
    { name: "Model", dataType: "text", id: 1, sortable: false },
    { name: "Model Kodu", dataType: "text", id: 0, sortable: false },
    {
      name: "Miktar",
      dataType: "number",
      id: 2,
      sortKey: "quantity",
      customSorter: (a, b) =>
        (Number(a.quantity) || 0) - (Number(b.quantity) || 0),
    },
    {
      name: "Oluşturulma",
      dataType: "date",
      id: 3,
      sortKey: "createdAt",
      customSorter: (a, b) => {
        const ta = new Date(a?.createdAt).getTime();
        const tb = new Date(b?.createdAt).getTime();
        return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
      },
    },
    { name: translate("UI.ACTIONS"), id: 4, sortable: false },
  ];

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("assembly-products-list", columns);

  const filterSections = useMemo(
    () => [
      buildPickerFilterSection({
        id: "model",
        title: "Model Filtresi",
        mode: "single",
        value: modelFilter || "",
        onChange: (v) => {
          setModelFilter(v ? String(v) : null);
          setPagination((prev) => ({ ...prev, page: 1 }));
        },
        options: sortedModels.map((model) => ({
          value: model._id,
          label: `${model.name} (${model.code})`,
          keywords: [model.code, model.name],
        })),
        allValue: "",
        allLabel: "Tüm Modeller",
        searchPlaceholder: "Model veya kod ara…",
      }),
    ],
    [modelFilter, sortedModels],
  );

  const activeFilterCount = useMemo(
    () => countTruthy([modelFilter]),
    [modelFilter],
  );

  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: "Ürünler", url: "/products" },
    { label: "Montaj" },
  ];

  useEffect(() => {
    fetchDevices();
  }, [pagination.page, pagination.limit, modelFilter, sort.sortBy, sort.sortOrder]);

  useEffect(() => {
    fetchDeviceModels();
  }, []);

  const fetchDevices = async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${API_URL}/api/assembly-inventory`, {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          sortBy: sort.sortBy,
          sortOrder: sort.sortOrder,
          ...(modelFilter && { modelId: modelFilter }),
        },
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      setAssemblyInventory(response.data.inventory || response.data.data || []);
      if (response.data.pagination) {
        setPagination({
          page: response.data.pagination.page || 1,
          limit: response.data.pagination.limit || 20,
          total: response.data.pagination.total || 0,
          totalPages: response.data.pagination.totalPages || 0,
          hasNextPage: response.data.pagination.hasNextPage || false,
          hasPrevPage: response.data.pagination.hasPrevPage || false,
        });
      }
    } catch (error) {
      console.error("Montaj envanteri getirilemedi:", error);
      setToasts([
        {
          description: "Montaj envanteri yüklenirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeviceModels = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/device-models`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      const active = response.data.filter((model) => model.status === "active");
      const sorted = [...active].sort((a, b) => {
        const byCode = compareCharByCharAZ(a?.code || "", b?.code || "");
        if (byCode !== 0) return byCode;
        return compareCharByCharAZ(a?.name || "", b?.name || "");
      });
      setModels(sorted);
    } catch (error) {
      console.error("Modeller getirilemedi:", error);
    }
  };

  const removeToast = (index) => {
    const values = toasts;
    values.splice(index, 1);
    setToasts([...values]);
  };

  const handleMoveToPackaging = async (inventory) => {
    setInventoryToUpdate(inventory);
    setSerialNumberInput("");
    setScannedSerials([]);
    setAutoGenerateQuantity("");
    setConfirmModalOpen(true);
    fetchExistingDevices(inventory.modelId._id);
  };

  const fetchExistingDevices = async (modelId) => {
    try {
      setLoadingExistingDevices(true);
      const response = await axios.get(`${API_URL}/api/devices`, {
        params: {
          modelId: modelId,
          status: "assembly",
          limit: 100,
        },
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      const devices = response.data.devices || [];
      setExistingDevices(devices.filter((d) => d.serialNumber));
    } catch (error) {
      console.error("Mevcut ürünler getirilemedi:", error);
      setExistingDevices([]);
    } finally {
      setLoadingExistingDevices(false);
    }
  };

  const handleAutoGenerate = async () => {
    if (!inventoryToUpdate) return;

    // Store the value and clear the field immediately
    const quantityStr = String(autoGenerateQuantity || "").trim();
    setAutoGenerateQuantity(""); // Clear immediately when function is called

    // Toplam izin verilen ve kalan miktar (montaj - mevcut seri numara lı ürünler)
    const totalAllowed = Math.max(
      0,
      inventoryToUpdate.quantity - existingDevices.length,
    );
    const remaining = Math.max(0, totalAllowed - scannedSerials.length);
    if (!quantityStr || quantityStr === "") {
      setToasts([
        {
          description: "Lütfen geçerli bir miktar girin (1'den büyük).",
          id: Date.now(),
          title: "Uyarı!",
          appearance: "warning",
        },
      ]);
      return;
    }

    const qty = parseInt(quantityStr, 10);
    if (isNaN(qty) || qty <= 0) {
      setToasts([
        {
          description: "Lütfen geçerli bir miktar girin (1'den büyük).",
          id: Date.now(),
          title: "Uyarı!",
          appearance: "warning",
        },
      ]);
      return;
    }

    // İzin verilenden fazla ekleme denemesi
    if (qty > remaining) {
      setToasts([
        {
          description: `Bu model için toplam en fazla ${totalAllowed} adet ürün paketlemeye alınabilir. Kalan eklenebilir miktar: ${remaining} adet.`,
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
      return;
    }

    try {
      setLoadingExistingDevices(true);
      const response = await axios.post(
        `${API_URL}/api/serial-numbers/generate`,
        {
          modelCode: inventoryToUpdate.modelId.code,
          qty: qty,
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      // Handle different response structures
      let serialNumbers = [];

      if (response.data) {
        // Case 1: Array of serial numbers
        if (Array.isArray(response.data)) {
          serialNumbers = response.data.map((item) =>
            typeof item === "string"
              ? item
              : item.serialNumber || item.serial || item,
          );
        }
        // Case 2: Array in serialNumbers property
        else if (Array.isArray(response.data.serialNumbers)) {
          serialNumbers = response.data.serialNumbers.map((item) =>
            typeof item === "string"
              ? item
              : item.serialNumber || item.serial || item,
          );
        }
        // Case 3: Single serialNumber object (current API structure)
        else if (response.data.serialNumber) {
          const serialValue =
            typeof response.data.serialNumber === "string"
              ? response.data.serialNumber
              : response.data.serialNumber.serialNumber;
          if (serialValue) {
            serialNumbers = [serialValue];
          }
        }
        // Case 4: Array in data property
        else if (Array.isArray(response.data.data)) {
          serialNumbers = response.data.data.map((item) =>
            typeof item === "string"
              ? item
              : item.serialNumber || item.serial || item,
          );
        }
        // Case 5: Single object with serialNumber property at root
        else if (
          response.data.serialNumber &&
          typeof response.data.serialNumber === "object"
        ) {
          serialNumbers = [response.data.serialNumber.serialNumber];
        }
      }

      // Normalize all serial numbers to uppercase and filter empty values
      serialNumbers = serialNumbers
        .map((s) => {
          if (!s) return "";
          return typeof s === "string"
            ? s.toUpperCase()
            : String(s).toUpperCase();
        })
        .filter((s) => s && s.trim() !== "");

      // Add to scanned serials, avoiding duplicates
      const currentSerialsUpper = scannedSerials.map((s) => s.toUpperCase());
      const newSerials = serialNumbers.filter(
        (s) => !currentSerialsUpper.includes(s.toUpperCase()),
      );

      if (newSerials.length > 0) {
        setScannedSerials((prev) => [...prev, ...newSerials]);
        setAutoGenerateQuantity("");

        setToasts([
          {
            description: `${newSerials.length} seri numarası otomatik oluşturuldu ve listeye eklendi.`,
            id: Date.now(),
            title: "Başarılı!",
            appearance: "success",
          },
        ]);
      } else {
        setToasts([
          {
            description:
              "Tüm oluşturulan seri numaraları zaten listede mevcut.",
            id: Date.now(),
            title: "Bilgi",
            appearance: "info",
          },
        ]);
      }
    } catch (error) {
      console.error("Seri numarası oluşturma hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Seri numaraları oluşturulamadı.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoadingExistingDevices(false);
    }
  };

  const handleSelectExistingDevice = (device) => {
    if (!inventoryToUpdate) return;

    const availableCount = Math.max(
      0,
      inventoryToUpdate.quantity -
        existingDevices.length -
        scannedSerials.length,
    );
    if (scannedSerials.length >= availableCount) {
      setToasts([
        {
          description: `Maksimum ${availableCount} adet ürün eklenebilir.`,
          id: Date.now(),
          title: "Uyarı!",
          appearance: "warning",
        },
      ]);
      return;
    }

    if (
      device.serialNumber &&
      !scannedSerials.includes(device.serialNumber.toUpperCase())
    ) {
      setScannedSerials([...scannedSerials, device.serialNumber.toUpperCase()]);
      setToasts([
        {
          description: `${device.serialNumber} eklendi.`,
          id: Date.now(),
          title: "Başarılı!",
          appearance: "success",
        },
      ]);
    }
  };

  const handleAddAllExistingDevices = () => {
    if (!inventoryToUpdate) return;

    const availableCount = Math.max(
      0,
      inventoryToUpdate.quantity -
        existingDevices.length -
        scannedSerials.length,
    );
    const serialsToAdd = (existingDevices || [])
      .map((d) => (d?.serialNumber ? d.serialNumber.toUpperCase() : null))
      .filter(Boolean)
      .filter((s) => !scannedSerials.includes(s))
      .slice(0, availableCount);

    if (serialsToAdd.length === 0) {
      setToasts([
        {
          description:
            availableCount === 0
              ? "Maksimum ürün sayısına ulaşıldı."
              : "Eklenecek yeni seri numarası yok.",
          id: Date.now(),
          title: "Bilgi",
          appearance: "info",
        },
      ]);
      return;
    }

    setScannedSerials([...scannedSerials, ...serialsToAdd]);
    setToasts([
      {
        description: `${serialsToAdd.length} seri numarası eklendi.`,
        id: Date.now(),
        title: "Başarılı!",
        appearance: "success",
      },
    ]);
  };

  const validateSerialNumberFormat = (serialNumber) => {
    const pattern = /^([A-Za-z0-9]+)\/(\d{2})(\d{2})\/(\d{4})$/;
    const match = serialNumber.match(pattern);

    if (match) {
      const modelCode = match[1].toUpperCase();
      const month = parseInt(match[2], 10);
      const year = parseInt(match[3], 10);
      const sequence = parseInt(match[4], 10);

      return { isValid: true, modelCode, year, month, sequence };
    }
    return { isValid: false };
  };

  const handleAddSerialNumber = () => {
    if (!serialNumberInput.trim()) {
      setToasts([
        {
          description: "Lütfen bir seri numarası girin.",
          id: Date.now(),
          title: "Uyarı!",
          appearance: "warning",
        },
      ]);
      return;
    }

    const formatCheck = validateSerialNumberFormat(serialNumberInput);

    if (!formatCheck.isValid) {
      setToasts([
        {
          description:
            "Geçersiz seri numarası formatı. Format: ModelCode/MMYY/SSSS",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
      return;
    }

    if (
      formatCheck.modelCode !== inventoryToUpdate?.modelId?.code?.toUpperCase()
    ) {
      setToasts([
        {
          description: `Seri numarası model kodunu (${formatCheck.modelCode}) seçili model (${inventoryToUpdate?.modelId?.code}) ile eşleşmiyor.`,
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
      return;
    }

    if (scannedSerials.includes(serialNumberInput.toUpperCase())) {
      setToasts([
        {
          description: "Bu seri numarası zaten eklendi.",
          id: Date.now(),
          title: "Uyarı!",
          appearance: "warning",
        },
      ]);
      return;
    }

    // Toplam izin verilen ve kalan miktar (montaj - mevcut seri numaralı ürünler)
    const totalAllowed = Math.max(
      0,
      inventoryToUpdate.quantity - existingDevices.length,
    );
    const remaining = Math.max(0, totalAllowed - scannedSerials.length);

    // Limit aşıldığında özel uyarı
    if (remaining <= 0) {
      setToasts([
        {
          description: `Bu model için toplam en fazla ${totalAllowed} adet ürün paketlemeye alınabilir. Eklenebilecek başka ürün kalmadı.`,
          id: Date.now(),
          title: "Uyarı!",
          appearance: "warning",
        },
      ]);
      return;
    }

    setScannedSerials([...scannedSerials, serialNumberInput.toUpperCase()]);
    setSerialNumberInput("");
  };

  const handleRemoveSerialNumber = async (serial) => {
    // Check if this is a newly generated serial (not from existing devices)
    const isExistingDevice = existingDevices.some(
      (d) => d.serialNumber?.toUpperCase() === serial.toUpperCase(),
    );

    // If it's a newly generated serial, delete it from database
    // (Devices are not created yet at this stage, only serial numbers exist)
    if (!isExistingDevice) {
      try {
        // Delete serial number using request body (to avoid URL encoding issues with slashes)
        await axios.delete(`${API_URL}/api/serial-numbers`, {
          data: {
            serialNumber: serial,
          },
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
        });
      } catch (error) {
        // If endpoint doesn't exist or fails, log but continue with UI removal
        if (error.response?.status !== 404) {
          console.warn("Serial number deletion warning:", error);
        }
        // Continue with removal from UI even if deletion fails
      }
    }

    // Remove from scanned serials list
    setScannedSerials(scannedSerials.filter((s) => s !== serial));
  };

  const handleAddToAssembly = async () => {
    if (!selectedModelForAdd) {
      setToasts([
        {
          description: "Lütfen bir model seçin.",
          id: Date.now(),
          title: "Uyarı!",
          appearance: "warning",
        },
      ]);
      return;
    }

    const qty = parseInt(quantityToAdd);
    if (isNaN(qty) || qty <= 0) {
      setToasts([
        {
          description: "Lütfen geçerli bir miktar girin (1'den büyük).",
          id: Date.now(),
          title: "Uyarı!",
          appearance: "warning",
        },
      ]);
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/assembly-inventory/increment`,
        {
          modelId: selectedModelForAdd,
          quantity: qty,
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      setToasts([
        {
          description: `${qty} adet ürün montaja eklendi.`,
          id: Date.now(),
          title: "Başarılı!",
          appearance: "success",
        },
      ]);

      fetchDevices();
      setShowAddSidebar(false);
      setSelectedModelForAdd(null);
      setQuantityToAdd("");
    } catch (error) {
      console.error("Montaja ekleme hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Ürünler montaja eklenemedi.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    }
  };

  const confirmMoveToPackaging = async () => {
    if (!inventoryToUpdate || scannedSerials.length === 0) {
      setToasts([
        {
          description: "Lütfen en az bir seri numarası ekleyin.",
          id: Date.now(),
          title: "Uyarı!",
          appearance: "warning",
        },
      ]);
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/part-assemblies/consume`,
        {
          modelId: inventoryToUpdate.modelId._id,
          quantity: scannedSerials.length,
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      // Create devices with scanned serial numbers
      const createPromises = scannedSerials.map(async (serialNumber) => {
        const formatCheck = validateSerialNumberFormat(serialNumber);

        // Create serial number if needed
        if (formatCheck.isValid) {
          try {
            const barcode = Math.floor(
              10000000000000 + Math.random() * 90000000000000,
            ).toString();
            await axios.post(
              `${API_URL}/api/serial-numbers`,
              {
                serialNumber: serialNumber,
                modelCode: formatCheck.modelCode,
                year: formatCheck.year,
                month: formatCheck.month,
                sequence: formatCheck.sequence,
                barcode,
                status: "available",
              },
              {
                headers: {
                  Authorization: `Bearer ${getToken()}`,
                },
              },
            );
          } catch (err) {
            // Ignore if already exists
            if (err.response?.status !== 409) {
              console.warn("Serial number creation warning:", err);
            }
          }
        }

        // Create device with status 'packaging'
        return axios.post(
          `${API_URL}/api/devices`,
          {
            serialNumber: serialNumber,
            modelId: inventoryToUpdate.modelId._id,
            status: "packaging",
            skipAssemblyPartConsumption: true,
          },
          {
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
          },
        );
      });

      await Promise.all(createPromises);

      // Decrement assembly inventory
      await axios.post(
        `${API_URL}/api/assembly-inventory/decrement`,
        {
          modelId: inventoryToUpdate.modelId._id,
          quantity: scannedSerials.length,
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      setToasts([
        {
          description: `${scannedSerials.length} ürün başarıyla paketlemeye alındı.`,
          id: Date.now(),
          title: "Başarılı!",
          appearance: "success",
        },
      ]);

      fetchDevices();
      setConfirmModalOpen(false);
      setInventoryToUpdate(null);
      setScannedSerials([]);
      setSerialNumberInput("");
    } catch (error) {
      console.error("Paketlemeye alma hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Ürünler paketlemeye alınamadı.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    }
  };

  const handleAutoGenerateClick = () => {
    if (loadingExistingDevices) {
      setToasts([
        {
          description: "İşlem devam ediyor, lütfen bekleyin.",
          id: Date.now(),
          title: "Bilgi",
          appearance: "info",
        },
      ]);
      return;
    }

    handleAutoGenerate();
  };

  const handleConfirmMoveToPackagingClick = () => {
    // confirmMoveToPackaging already validates scannedSerials and limits; we just keep the button clickable.
    confirmMoveToPackaging();
  };

  return (
    <AppPage
      title="Montaj"
      breadcrumbs={breadcrumbs}
      actions={
        canWrite ? (
          <Button
            appearance="primary-animated"
            onClick={() => {
              setSelectedModelForAdd(null);
              setQuantityToAdd("");
              setShowAddSidebar(true);
            }}
          >
            Montaja Ürün Ekle
          </Button>
        ) : null
      }
    >
      <Container>
        <ContainerItem sm={4} md={8} xl={12}>
          <Table
            columns={columns || []}
            visibleColumns={visibleColumns}
            tableId="assembly-products-list"
            loading={loading}
            search={true}
            externalSorting={true}
            onSort={(nextSort) => {
              setSort(nextSort);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            hideFooter={true}
            viewOptions={
              <ListViewOptionsPanel
                columns={columns}
                visibleColumns={visibleColumns}
                onToggleColumn={toggleColumnVisibility}
                onResetColumns={resetToDefault}
                filterSections={filterSections}
                activeFilterCount={activeFilterCount}
                onReset={() => {
                  setModelFilter(null);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                showApply={false}
              />
            }
          >
            {(assemblyInventory || []).map((inventory) => (
              <Tr key={inventory._id} data={inventory}>
                {visibleColumns.includes(1) && (
                  <Td>{inventory.modelId?.name || "-"}</Td>
                )}
                {visibleColumns.includes(0) && (
                  <Td>{inventory.modelId?.code || "-"}</Td>
                )}
                {visibleColumns.includes(2) && (
                  <Td>
                    <div
                      style={{
                        backgroundColor: "#3B82F615",
                        color: "#3B82F6",
                        padding: "5px 10px",
                        borderRadius: "5px",
                        width: "fit-content",
                        fontWeight: "600",
                      }}
                    >
                      {inventory.quantity} adet
                    </div>
                  </Td>
                )}
                {visibleColumns.includes(3) && (
                  <Td>{formatInstantDateShortTr(inventory.createdAt)}</Td>
                )}
                {visibleColumns.includes(4) && (
                  <Td>
                    <div className="d-flex gap-2">
                      {canWrite && (
                        <div
                          className="d-flex align-items-center gap-1"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleMoveToPackaging(inventory)}
                          title="Montaj → Paketleme: Ürünü paketleme aşamasına geçir"
                        >
                          <FiCheckCircle
                            style={{
                              fontSize: "20px",
                              color: "#EAB308",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#EAB308",
                              fontWeight: "600",
                            }}
                          >
                            Paketleme
                          </span>
                        </div>
                      )}
                    </div>
                  </Td>
                )}
              </Tr>
            ))}
          </Table>

          {!loading && pagination.total > 0 && (
            <div className="mt-3">
              <Pagination
                totalItems={pagination.total}
                totalPerPage={pagination.limit}
                currentPage={pagination.page}
                totalRecords={pagination.total}
                onPageChange={({ pagination: paginationData }) => {
                  setPagination((prev) => ({
                    ...prev,
                    page: paginationData.currentPage,
                    limit: paginationData.itemsPerPage,
                  }));
                }}
                options={{
                  skipPrevious: true,
                  previous: true,
                  next: true,
                  skipNext: true,
                  selectQuantity: true,
                  simpleCounting: false,
                }}
              />
            </div>
          )}
        </ContainerItem>
      </Container>

      <ToastContainer>
        {toasts?.map((toast, index) => (
          <Toast
            {...toast}
            key={index}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
          />
        ))}
      </ToastContainer>

      <Modal
        isOpen={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setInventoryToUpdate(null);
          setScannedSerials([]);
          setSerialNumberInput("");
          setAutoGenerateQuantity("");
          setExistingDevices([]);
        }}
        width="medium"
      >
        <ModalBody showCloseButton={false}>
          <div
            id="modal-header"
            className={modalStyles["modal-body-inline-header"]}
          >
            <h4
              className={modalStyles["modal-content__header--title"]}
              style={{
                margin: 0,
                flex: 1,
                minWidth: 0,
                paddingRight: 12,
              }}
            >
              Paketlemeye Al - {inventoryToUpdate?.modelId?.name} (
              {inventoryToUpdate?.modelId?.code})
            </h4>
            <ModalClose ariaLabel="Kapat" iconSize={18} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {/* Auto Generate Section */}
            <div
              className="p-3"
              style={{
                borderRadius: 10,
                border: "1px solid #e9ecef",
                background: "#f8f9fa",
              }}
            >
              <div
                className="d-flex align-items-center justify-content-between mb-2"
                style={{ gap: 12 }}
              >
                <Label className="mb-0">Otomatik seri numarası oluştur</Label>
              </div>
              <div
                className="d-flex gap-2 align-items-end"
                style={{ flexWrap: "wrap" }}
              >
                <div style={{ minWidth: 140 }}>
                  {(() => {
                    const availableCount = Math.max(
                      0,
                      (inventoryToUpdate?.quantity ?? 0) -
                        (existingDevices?.length ?? 0) -
                        scannedSerials.length,
                    );
                    return (
                      <Input
                        type="number"
                        className="form-control"
                        value={autoGenerateQuantity}
                        onChange={(value) => {
                          const val = value ?? "";
                          setAutoGenerateQuantity(val);
                        }}
                        placeholder="Örn: 5"
                        min="1"
                        max={availableCount}
                      />
                    );
                  })()}
                </div>
                <Button appearance="primary" onClick={handleAutoGenerateClick}>
                  {loadingExistingDevices ? "Oluşturuluyor..." : "Oluştur"}
                </Button>
              </div>
            </div>

            {/* Manual Input Section */}
            <div
              className="p-3"
              style={{
                borderRadius: 10,
                border: "1px solid #e9ecef",
                background: "#fff",
              }}
            >
              <Label className="mb-1">Manuel seri numarası ekle</Label>
              <div
                style={{
                  color: "#6c757d",
                  fontWeight: 600,
                  fontSize: 12,
                  marginBottom: 10,
                }}
              >
                Format: {inventoryToUpdate?.modelId?.code}/MMYY/SSSS
              </div>
              <div
                className="d-flex gap-2 align-items-end"
                style={{ flexWrap: "wrap" }}
              >
                <div style={{ flex: "1 1 280px", minWidth: 260 }}>
                  <Label className="mb-1">Seri numarası</Label>
                  <Input
                    type="text"
                    className="form-control"
                    value={serialNumberInput}
                    onChange={(value) => setSerialNumberInput(value ?? "")}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") handleAddSerialNumber();
                    }}
                    placeholder={`Örn: ${inventoryToUpdate?.modelId?.code}/0124/0001`}
                  />
                </div>
                <Button appearance="primary" onClick={handleAddSerialNumber}>
                  Ekle
                </Button>
              </div>
            </div>

            {/* Existing Devices Section */}
            {existingDevices.length > 0 && (
              <div
                className="p-3"
                style={{
                  borderRadius: 10,
                  border: "1px solid #e9ecef",
                  background: "#fff",
                }}
              >
                <div
                  className="d-flex align-items-center justify-content-between mb-2"
                  style={{ gap: 12 }}
                >
                  <Label className="mb-0">
                    Mevcut ürünler{" "}
                    <span style={{ color: "#6c757d" }}>
                      ({existingDevices.length})
                    </span>
                  </Label>
                  <Button
                    appearance="primary-subtle"
                    size="sm"
                    onClick={handleAddAllExistingDevices}
                  >
                    Tümünü Ekle
                  </Button>
                </div>
                <div
                  style={{
                    maxHeight: 180,
                    overflowY: "auto",
                    border: "1px solid #f1f3f5",
                    borderRadius: 10,
                    padding: 10,
                    background: "#fafbfc",
                  }}
                >
                  {existingDevices.map((device) => {
                    const isSelected = scannedSerials.includes(
                      device.serialNumber?.toUpperCase(),
                    );
                    return (
                      <div
                        key={device._id}
                        className="d-flex justify-content-between align-items-center p-2 mb-1"
                        style={{
                          backgroundColor: isSelected ? "#E6F4EA" : "#fff",
                          borderRadius: 10,
                          border: "1px solid #eef1f4",
                          cursor: isSelected ? "default" : "pointer",
                          opacity: isSelected ? 0.75 : 1,
                        }}
                        onClick={() =>
                          !isSelected && handleSelectExistingDevice(device)
                        }
                        title={
                          isSelected ? "Bu seri numarası zaten seçildi" : "Ekle"
                        }
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            lineHeight: 1.2,
                          }}
                        >
                          <span style={{ fontWeight: 700, color: "#212529" }}>
                            {device.serialNumber}
                          </span>
                          {isSelected && (
                            <small
                              style={{
                                color: "#198754",
                                fontWeight: 700,
                                marginTop: 2,
                              }}
                            >
                              Seçildi
                            </small>
                          )}
                        </div>
                        {!isSelected && (
                          <Button
                            appearance="primary-subtle"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectExistingDevice(device);
                            }}
                          >
                            Ekle
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Selected Serials Section */}
            {scannedSerials.length > 0 && (
              <div
                className="p-3"
                style={{
                  borderRadius: 10,
                  border: "1px solid #e9ecef",
                  background: "#fff",
                }}
              >
                <div
                  className="d-flex align-items-center justify-content-between mb-2"
                  style={{ gap: 12 }}
                >
                  <Label className="mb-0">
                    Seçilen seri numaraları{" "}
                    <span style={{ color: "#6c757d" }}>
                      ({scannedSerials.length})
                    </span>
                  </Label>
                  {(() => {
                    const availableCount = Math.max(
                      0,
                      (inventoryToUpdate?.quantity ?? 0) -
                        (existingDevices?.length ?? 0),
                    );
                    const remainingCount = Math.max(
                      0,
                      availableCount - scannedSerials.length,
                    );
                    return (
                      <small style={{ color: "#6c757d", fontWeight: 700 }}>
                        Kalan: {remainingCount}
                      </small>
                    );
                  })()}
                </div>
                <div style={{ maxHeight: 220, overflowY: "auto" }}>
                  {scannedSerials.map((serial, index) => (
                    <div
                      key={serial}
                      className="d-flex justify-content-between align-items-center p-2 mb-1"
                      style={{
                        backgroundColor: "#f8f9fa",
                        borderRadius: 10,
                        border: "1px solid #eef1f4",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            minWidth: 26,
                            height: 26,
                            borderRadius: 8,
                            background: "#2125290D",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 800,
                            fontSize: 12,
                            color: "#212529",
                          }}
                        >
                          {index + 1}
                        </span>
                        <span style={{ fontWeight: 700, color: "#212529" }}>
                          {serial}
                        </span>
                      </div>
                      <Button
                        appearance="danger-subtle"
                        size="sm"
                        onClick={() => handleRemoveSerialNumber(serial)}
                      >
                        Kaldır
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="primary"
              onClick={handleConfirmMoveToPackagingClick}
            >
              Paketlemeye Al ({scannedSerials.length} adet)
            </Button>
            <Button
              appearance="subtle"
              onClick={() => {
                setConfirmModalOpen(false);
                setInventoryToUpdate(null);
                setScannedSerials([]);
                setSerialNumberInput("");
                setAutoGenerateQuantity("");
                setExistingDevices([]);
              }}
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <SidebarContainer
        show={showAddSidebar}
        title="Montaja Ürün Ekle"
        onClose={() => {
          setShowAddSidebar(false);
          setSelectedModelForAdd(null);
          setQuantityToAdd("");
        }}
      >
        <div className="d-flex flex-column" style={{ height: "100%" }}>
          <div className="mb-4">
            <Label>Model</Label>
            <div
              style={{
                background: "#fff",
                border: "1px solid #e2e5e8",
                borderRadius: "6px",
                transition: "all 0.2s ease",
                marginTop: "8px",
              }}
            >
              <Dropdown
                placement="bottom-start"
                trigger={
                  <div className="d-flex align-items-center justify-content-between p-2 w-100">
                    <span>
                      {selectedModelForAdd
                        ? models.find((m) => m._id === selectedModelForAdd)
                            ?.name || "Model seçin"
                        : "Model seçin"}
                    </span>
                  </div>
                }
              >
                <DropdownContent className="w-100">
                  {sortedModels.map((model) => (
                    <DropdownItem
                      key={model._id}
                      onClick={() => {
                        setSelectedModelForAdd(model._id);
                      }}
                      active={model._id === selectedModelForAdd}
                    >
                      {model.name} ({model.code})
                    </DropdownItem>
                  ))}
                </DropdownContent>
              </Dropdown>
            </div>
          </div>
          <div className="mb-4">
            <Label>Miktar</Label>
            <Input
              type="number"
              className="form-control"
              value={quantityToAdd}
              onChange={(value) => setQuantityToAdd(value ?? "")}
              placeholder="Adet girin"
              min="1"
              style={{ marginTop: "8px" }}
            />
          </div>
          <div className="mt-auto pt-4">
            <ButtonGroup placement="end" reverse>
              <Button appearance="primary" onClick={handleAddToAssembly}>
                Ekle
              </Button>
              <Button
                appearance="subtle"
                onClick={() => {
                  setShowAddSidebar(false);
                  setSelectedModelForAdd(null);
                  setQuantityToAdd("");
                }}
              >
                İptal
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </SidebarContainer>
    </AppPage>
  );
};

export default AssemblyProducts;
